import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconChevronLeft, IconChevronRight, IconPlus, IconX, IconAlertTriangle, IconPencil } from "@tabler/icons-react";
import "./appointments.scss";
import { leaveOn, useClinicData } from "../../context/ClinicDataContext";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import NewAppointmentModal from "../../components/NewAppointmentModal/NewAppointmentModal";
import { NEXT_STEP } from "../Dashboard/DashboardPage";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import {
  addDays,
  dayName,
  formatDateLong,
  formatDateShort,
  formatTime,
  minutesToTime,
  timeToMinutes,
} from "../../utils/format";
import { formatPhone } from "../../utils/phone";

const ROW_HEIGHT = 40; // px per slot

const AppointmentsPage = () => {
  const { today, clinic, appointments, dentists, patientById, dentistById, apptDuration, apptLabel, updateAppointment } =
    useClinicData();
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || today;
  const [selectedId, setSelectedId] = useState(null);
  const [newDefaults, setNewDefaults] = useState(null);
  const [editing, setEditing] = useState(null);

  const setDate = (value) => {
    setSelectedId(null);
    setParams(value === today ? {} : { date: value });
  };

  const open = timeToMinutes(clinic.openTime);
  const close = timeToMinutes(clinic.closeTime);
  const slots = useMemo(() => {
    const list = [];
    for (let m = open; m < close; m += clinic.slotMinutes) list.push(minutesToTime(m));
    return list;
  }, [open, close, clinic.slotMinutes]);

  const day = dayName(date);
  const clinicClosed = !clinic.openDays.includes(day);
  const dayAppointments = appointments.filter((a) => a.date === date);
  // Active dentists, plus any deactivated dentist who still has visits on this day.
  const columns = dentists.filter((d) => d.active || dayAppointments.some((a) => a.dentistId === d.id));
  const selected = appointments.find((a) => a.id === selectedId);
  const counts = {
    total: dayAppointments.filter((a) => a.status !== "cancelled").length,
    unconfirmed: dayAppointments.filter((a) => a.status === "scheduled").length,
  };

  const blockStyle = (appt) => {
    const start = timeToMinutes(appt.start) - open;
    const duration = apptDuration(appt);
    return {
      top: (start / clinic.slotMinutes) * ROW_HEIGHT + 2,
      height: (duration / clinic.slotMinutes) * ROW_HEIGHT - 4,
      "--dentist-color": dentistById[appt.dentistId]?.color,
    };
  };

  return (
    <div className="appointments-page">
      <div className="toolbar">
        <div className="toolbar__group">
          <button className="btn btn--secondary btn--icon" type="button" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>
            <IconChevronLeft size={20} stroke={1.75} />
          </button>
          <button className="btn btn--secondary" type="button" onClick={() => setDate(today)} disabled={date === today}>
            Today
          </button>
          <button className="btn btn--secondary btn--icon" type="button" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>
            <IconChevronRight size={20} stroke={1.75} />
          </button>
          <div className="appointments-page__date">
            <h2>{formatDateLong(date)}</h2>
            <span>
              {clinicClosed
                ? "Clinic closed"
                : `${counts.total} appointments${counts.unconfirmed ? `, ${counts.unconfirmed} unconfirmed` : ""}`}
            </span>
          </div>
        </div>
        <div className="toolbar__group">
          <label className="appointments-page__jump">
            <span className="visually-hidden">Go to date</span>
            <input className="form-field__control" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          <button className="btn btn--primary" type="button" onClick={() => setNewDefaults({ date })}>
            <IconPlus size={18} stroke={2} />
            New appointment
          </button>
        </div>
      </div>

      <div className={`appointments-page__layout ${selected ? "appointments-page__layout--with-panel" : ""}`}>
        <div className="day-grid" style={{ "--row-height": `${ROW_HEIGHT}px` }}>
          <div className="day-grid__scroll">
            <div className="day-grid__head" style={{ gridTemplateColumns: `84px repeat(${columns.length}, minmax(200px, 1fr))` }}>
              <span />
              {columns.map((d) => (
                <div className="day-grid__dentist" key={d.id}>
                  <span className="avatar" style={{ background: d.color, color: "#fff" }}>
                    {d.initials}
                  </span>
                  <span>
                    <strong>{d.name}</strong>
                    <span>{d.role}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="day-grid__body" style={{ gridTemplateColumns: `84px repeat(${columns.length}, minmax(200px, 1fr))` }}>
              <div className="day-grid__times">
                {slots.map((t) => (
                  <span className="day-grid__time" key={t}>
                    {t.endsWith(":00") ? formatTime(t) : ""}
                  </span>
                ))}
              </div>

              {columns.map((dentist) => {
                const leave = leaveOn(dentist, date);
                const off = clinicClosed || !dentist.active || Boolean(leave) || !dentist.days.includes(day);
                const mine = dayAppointments.filter((a) => a.dentistId === dentist.id);
                return (
                  <div className={`day-grid__column ${off ? "day-grid__column--off" : ""}`} key={dentist.id}>
                    {slots.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className="day-grid__slot"
                        disabled={off}
                        aria-label={`Book ${dentist.name} at ${formatTime(t)}`}
                        onClick={() => setNewDefaults({ date, dentistId: dentist.id, start: t })}
                      />
                    ))}

                    {off && (
                      <span className="day-grid__off-label">
                        {clinicClosed ? "Clinic closed" : leave ? leave.type : !dentist.active ? "Inactive" : "Not working today"}
                      </span>
                    )}

                    {mine.map((appt) => {
                      const patient = patientById[appt.patientId];
                      const inactive = appt.status === "cancelled" || appt.status === "no-show";
                      const duration = apptDuration(appt);
                      const size = duration <= 15 ? "tiny" : duration <= 45 ? "short" : "";
                      return (
                        <button
                          key={appt.id}
                          type="button"
                          className={`appt-block appt-block--${appt.status} ${size ? `appt-block--${size}` : ""} ${inactive ? "appt-block--inactive" : ""} ${
                            appt.id === selectedId ? "appt-block--selected" : ""
                          }`}
                          style={blockStyle(appt)}
                          onClick={() => setSelectedId(appt.id)}
                        >
                          <span className="appt-block__time">
                            {formatTime(appt.start)}
                            {patient.alerts.length > 0 && (
                              <IconAlertTriangle className="appt-block__alert" size={14} stroke={2} aria-label="Has medical alert" />
                            )}
                          </span>
                          <span className="appt-block__patient">{patient.name}</span>
                          <span className="appt-block__treatment">{apptLabel(appt)}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {selected && (
          <AppointmentPanel
            appt={selected}
            onClose={() => setSelectedId(null)}
            onUpdate={(changes) => updateAppointment(selected.id, changes)}
            onEdit={() => setEditing(selected)}
          />
        )}
      </div>

      <div className="appointments-page__legend" aria-label="Status legend">
        {["scheduled", "confirmed", "checked-in", "in-treatment", "completed", "cancelled"].map((s) => (
          <StatusBadge key={s} status={s} />
        ))}
      </div>

      {editing && (
        <NewAppointmentModal
          appointment={editing}
          onClose={() => setEditing(null)}
          onSaved={(appt) => {
            if (appt.date !== date) setDate(appt.date);
            setSelectedId(appt.id);
          }}
        />
      )}

      {newDefaults && (
        <NewAppointmentModal
          defaults={newDefaults}
          onClose={() => setNewDefaults(null)}
          onSaved={(appt) => {
            if (appt.date !== date) setDate(appt.date);
            setSelectedId(appt.id);
          }}
        />
      )}
    </div>
  );
};

const AppointmentPanel = ({ appt, onClose, onUpdate, onEdit }) => {
  const { patientById, dentistById, apptTreatments, apptDuration } = useClinicData();
  const editable = !appt.checkedOut && appt.status !== "cancelled" && appt.status !== "no-show";
  const patient = patientById[appt.patientId];
  const treatments = apptTreatments(appt);
  const dentist = dentistById[appt.dentistId];
  const step = NEXT_STEP[appt.status];
  const end = minutesToTime(timeToMinutes(appt.start) + apptDuration(appt));
  const closed = appt.status === "completed" || appt.status === "cancelled" || appt.status === "no-show";

  return (
    <aside className="appt-panel" aria-label="Appointment details">
      <div className="appt-panel__header">
        <StatusBadge status={appt.status} />
        <button className="btn btn--ghost btn--icon" type="button" aria-label="Close details" onClick={onClose}>
          <IconX size={20} stroke={1.75} />
        </button>
      </div>

      <h3 className="appt-panel__patient">{patient.name}</h3>
      <p className="appt-panel__phone">{formatPhone(patient.phone)}</p>

      {patient.alerts.length > 0 && (
        <div className="appt-panel__alerts">
          {patient.alerts.map((alert) => (
            <span className="badge badge--danger" key={alert}>
              {alert}
            </span>
          ))}
        </div>
      )}

      <dl className="appt-panel__details">
        <div>
          <dt>{treatments.length > 1 ? "Treatments" : "Treatment"}</dt>
          <dd>
            {treatments.map((t) => (
              <span className="appt-panel__treatment" key={t.id}>
                {t.name}
              </span>
            ))}
          </dd>
        </div>
        <div>
          <dt>Time</dt>
          <dd>
            {formatTime(appt.start)} – {formatTime(end)}
          </dd>
        </div>
        <div>
          <dt>Dentist</dt>
          <dd>{dentist.name}</dd>
        </div>
        {appt.rescheduledFrom && (
          <div>
            <dt>Moved from</dt>
            <dd className="appt-panel__moved">
              {formatDateShort(appt.rescheduledFrom.date)}, {formatTime(appt.rescheduledFrom.start)}
            </dd>
          </div>
        )}
        {appt.notes && (
          <div>
            <dt>Notes</dt>
            <dd>{appt.notes}</dd>
          </div>
        )}
      </dl>

      <div className="appt-panel__actions">
        {step && (
          <button className="btn btn--primary" type="button" onClick={() => onUpdate({ status: step.to })}>
            {step.label}
          </button>
        )}
        {appt.status === "completed" && !appt.checkedOut && (
          <Link className="btn btn--primary" to={`${BASE_PATH}/checkout?appt=${appt.id}`}>
            Check out
          </Link>
        )}
        {editable && (
          <button className="btn btn--secondary" type="button" onClick={onEdit}>
            <IconPencil size={18} stroke={1.75} />
            Edit or reschedule
          </button>
        )}
        <Link className="btn btn--secondary" to={`${BASE_PATH}/patients/${patient.id}`}>
          Open patient record
        </Link>
        {!closed && (
          <div className="appt-panel__secondary">
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => onUpdate({ status: "no-show" })}>
              Mark no-show
            </button>
            <button className="btn btn--danger btn--sm" type="button" onClick={() => onUpdate({ status: "cancelled" })}>
              Cancel appointment
            </button>
          </div>
        )}
        {(appt.status === "cancelled" || appt.status === "no-show") && (
          <button className="btn btn--ghost btn--sm" type="button" onClick={() => onUpdate({ status: "scheduled" })}>
            Restore appointment
          </button>
        )}
      </div>
    </aside>
  );
};

export default AppointmentsPage;
